import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { ARCHIVE } from "../data/content";
import { SectionLabel, Reveal } from "./Reveal";

const spanClass = {
  tall: "md:row-span-2 aspect-[3/4]",
  wide: "md:col-span-2 aspect-[16/10]",
  square: "aspect-square",
};

export default function Archive() {
  const [active, setActive] = useState(null);

  return (
    <section id="archive" className="relative w-full py-24 md:py-36 px-5 md:px-10" data-testid="section-archive">
      <div className="max-w-[1400px] mx-auto">
        <SectionLabel index="03 / ARCHIVE" id="archive-label">PHOTOGRAPHY</SectionLabel>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
          <h2 className="font-display chrome-text text-5xl sm:text-6xl md:text-8xl leading-[0.9]">
            <Reveal text="ARCHIVE" />
          </h2>
          <p className="font-body text-white/50 max-w-sm text-sm md:text-base">
            Portraits, film stills and covers from the world of H.Λ.R.V.E.R.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5 auto-rows-auto">
          {ARCHIVE.map((p, i) => (
            <motion.button
              key={p.id}
              onClick={() => setActive(p)}
              data-testid={`archive-item-${p.id}`}
              data-cursor
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-6% 0px" }}
              transition={{ duration: 0.8, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className={`group relative overflow-hidden rounded-sm ring-1 ring-white/10 ${spanClass[p.span] || "aspect-square"}`}
            >
              <img
                src={p.src}
                alt={p.tag}
                className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/20 group-hover:bg-black/0 transition-colors duration-500" />
              <div className="absolute bottom-0 left-0 p-4 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                <span className="track-label text-white/80">{p.tag}</span>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            className="fixed inset-0 z-[9600] flex items-center justify-center p-6 bg-black/92 backdrop-blur-2xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            data-testid="archive-lightbox"
          >
            <motion.img
              src={active.src}
              alt={active.tag}
              className="max-h-[85vh] max-w-full object-contain ring-1 ring-white/15"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setActive(null)}
              data-testid="archive-lightbox-close"
              className="absolute top-5 right-5 w-11 h-11 rounded-full glass flex items-center justify-center text-white/80 hover:text-white"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
